import { ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import { t } from "@galaxy-io/dls/theme/tokens/t";
import type { Integration } from "@gtm/contracts";
import { styled } from "@linaria/react";
import type { ComponentProps, ReactNode } from "react";
import { useUrlState } from "@/app/navigation";
import { Button } from "@/components/ui/button";
import { DrawerList, DrawerRow } from "@/components/workspace/drawer-content";
import { Status } from "@/components/workspace/status";
import { SurfaceDialog } from "@/components/workspace/surface-dialog";
import { DeleteIntegration } from "./delete-integration";
import { IntegrationDrawerContent } from "./integration-drawer-content";

const ConnectionFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: ${t.space[12]};
`;

/** Connection details stay in the drawer while configuration opens in a modal. */
export function ConnectionSurface({
  selected,
  details,
  children,
  onClose,
  ...props
}: Omit<ComponentProps<typeof SurfaceDialog>, "drawer"> & {
  selected?: Integration;
  details?: ReactNode;
}) {
  const { params, update } = useUrlState();
  const editing = !selected || params.get("integrationEdit") === "1";
  return (
    <>
      {selected && (
        <SurfaceDialog
          open={props.open}
          drawer
          flush
          title={selected.name}
          headerLeading={props.headerLeading}
          onClose={onClose}
          footer={
            <ConnectionFooter>
              <DeleteIntegration integration={selected} disabled={props.busy} onDeleted={onClose} />
              <Button
                label="Edit connection"
                variant={ButtonVariant.SECONDARY}
                isDisabled={props.busy}
                onClick={() => update({ integrationEdit: "1" })}
              />
            </ConnectionFooter>
          }
        >
          <IntegrationDrawerContent integrationId={selected.id}>
            <DrawerList>
              <DrawerRow label="Status">
                <Status value={selected.enabled ? "connected" : "disconnected"} />
              </DrawerRow>
              {selected.provider !== "posthog" &&
                selected.account &&
                selected.account !== selected.name && (
                  <DrawerRow
                    label={
                      selected.provider === "slack"
                        ? "Channel"
                        : selected.provider === "webhook"
                          ? "Endpoint"
                          : "Account"
                    }
                  >
                    {selected.account}
                  </DrawerRow>
                )}
              {selected.provider === "slack" && (
                <DrawerRow label="Default destination">
                  {selected.isDefault ? "Yes" : "No"}
                </DrawerRow>
              )}
            </DrawerList>
            {details}
          </IntegrationDrawerContent>
        </SurfaceDialog>
      )}
      {
        <SurfaceDialog
          {...props}
          open={props.open && editing}
          title={selected ? `Edit ${selected.name}` : props.title}
          onClose={selected ? () => update({ integrationEdit: null }) : onClose}
          footer={
            <>
              <Button
                label="Cancel"
                variant={ButtonVariant.SECONDARY}
                isDisabled={props.busy}
                onClick={selected ? () => update({ integrationEdit: null }) : onClose}
              />
              {props.footer}
            </>
          }
        >
          {children}
        </SurfaceDialog>
      }
    </>
  );
}
